import { type ReactElement } from 'react';
import { Box, Card, CardContent, FeatureErrorBoundary } from '@citibox-panel/ui';
import { PageIntro } from './PageIntro';
import { CoverageCard } from './CoverageCard';
import { PermissionSummary } from './PermissionSummary';
import { BatchCard } from './BatchCard';
import { LookupCard } from './LookupCard';
import { type DoorAccessLocation } from '../lib/doorAccessLocation';
import { neverHandledByHost, type IsHandledByHost } from '../../../lib/hostErrors';

export interface AmazonDoorAccessPageProps {
  // Injected by the host, which is what picks mock or real: the slice carries no client and no env
  // wiring, and therefore no dependency on the api-client at all.
  fetchDoorAccessCount: (enabled: boolean) => Promise<number>;
  setDoorAccess: (locationIds: number[], enabled: boolean) => Promise<void>;
  fetchDoorAccessLocation: (locationId: number) => Promise<DoorAccessLocation>;
  // The BARE market label the confirmation names («ES»), not the band's pill text. `null` is a
  // deployment that cannot be trusted to name its market.
  marketLabel: string | null;
  // Set by the host only for a move made from inside the panel.
  focusHeadingOnArrival?: boolean;
  // Errors the host already answers with a screen of its own, so no card paints them a second time.
  // Left out, every error stays on its card.
  isHandledByHost?: IsHandledByHost;
}

// The band and the navigation around this screen are the host's layout: this module resolves no
// config and knows no route. It opens no `main` landmark — the host's authenticated layout does.
export function AmazonDoorAccessPage({
  fetchDoorAccessCount,
  setDoorAccess,
  fetchDoorAccessLocation,
  marketLabel,
  focusHeadingOnArrival = false,
  isHandledByHost = neverHandledByHost,
}: AmazonDoorAccessPageProps): ReactElement {
  return (
    // One white, rounded panel for everything the page says — same surface, `m: 2` inset and default
    // `CardContent` padding as every other card at this level. Outlined, per the house pattern.
    <Card variant="outlined" sx={{ borderRadius: 3, m: 2 }}>
      <CardContent>
        <PageIntro focusHeadingOnArrival={focusHeadingOnArrival} />
        {/* Two columns on the 8px scale. DOM order is the reading order and the tab order. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 3,
            alignItems: 'start',
          }}
        >
          {/* Per-card boundary: a card that throws takes itself and nothing else. The sentence sits
              OUTSIDE it: static copy has nothing to throw. */}
          <Box>
            <PermissionSummary />
            <FeatureErrorBoundary>
              <CoverageCard fetchCount={fetchDoorAccessCount} isHandledByHost={isHandledByHost} />
            </FeatureErrorBoundary>
            {/* The grid's gap separates columns, not what sits inside one. */}
            <Box sx={{ mt: 3 }}>
              <FeatureErrorBoundary>
                <LookupCard
                  fetchLocation={fetchDoorAccessLocation}
                  isHandledByHost={isHandledByHost}
                />
              </FeatureErrorBoundary>
            </Box>
          </Box>
          <FeatureErrorBoundary>
            <BatchCard
              marketLabel={marketLabel}
              setDoorAccess={setDoorAccess}
              isHandledByHost={isHandledByHost}
            />
          </FeatureErrorBoundary>
        </Box>
      </CardContent>
    </Card>
  );
}
